import { ExerciseStateMachine } from './ExerciseStateMachine';

export const SeatedKneeExtensionsState = Object.freeze({
  FLEXED: 'FLEXED',           
  EXTENDING: 'EXTENDING',     
  EXTENDED: 'EXTENDED',       
  FLEXING: 'FLEXING'          
});

const MOVEMENT_THRESHOLD = 1.0;       
const MIN_FRAMES_IN_POSITION = 3;     
const MIN_EXTENSION_ANGLE = 90;       
const TARGET_EXTENSION_ANGLE = 150;   
const ANGLE_CHANGE_THRESHOLD = 5;     
const JOINT_STABILITY_THRESHOLD = 0.03; 
const INTERNAL_COOLDOWN_FRAMES = 10;  
const SEATED_POSTURE_THRESHOLD = 0.3; 
const MIN_CONFIDENCE_THRESHOLD = 0.35; 
const CALIBRATION_ANGLE_THRESHOLD = 135; 
const STABLE_FRAMES_FOR_LEG_SWITCH = 6; 
const AUTO_DETECT_LEGS = true;        

export class SeatedKneeExtensionsFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: SeatedKneeExtensionsState.FLEXED,
      initialStateData: {
        activeLeg: 'right',            
        previousKneeAngles: { right: 0, left: 0 },
        lastAngles: { right: [], left: [] },
        lastFlexedFrame: { right: 0, left: 0 },
        lastExtendedFrame: { right: 0, left: 0 },
        framesSinceLastFlexed: { right: 0, left: 0 },
        framesSinceLastExtended: { right: 0, left: 0 },
        calibrated: { right: false, left: false },
        calibrationData: {
          right: { restingAngle: 0, targetAngle: 0, minAngle: 0 },
          left: { restingAngle: 0, targetAngle: 0, minAngle: 0 }
        },
        maxExtensionAngle: { right: 0, left: 0 },
        consecutiveHighAngleFrames: { right: 0, left: 0 },
        stableFlexedFrames: { right: 0, left: 0 },
        stableExtendedFrames: { right: 0, left: 0 },
        hipStabilityScore: { right: 100, left: 100 },
        controlScore: { right: 100, left: 100 },
        extensionScore: { right: 0, left: 0 },
        previousHipPosition: { right: null, left: null },
        inInternalCooldown: { right: false, left: false },
        cooldownFramesRemaining: { right: 0, left: 0 },
        positionValid: true,
        frameCounter: 0,             
        lastValidMetrics: { right: null, left: null },
        kalmanFilters: {
          right: { x: 0, P: 1, Q: 0.01, R: 0.1 }, 
          left: { x: 0, P: 1, Q: 0.01, R: 0.1 }
        },
        consecutiveEmptyFrames: { right: 0, left: 0 },
        legScores: { right: 0, left: 0 }  
      },
      initialFormScores: {
        overall: 100,
        rangeOfMotion: 0,
        control: 100,
        balance: 100
      },
      transitions: {
        [SeatedKneeExtensionsState.FLEXED]: [
          {
            targetState: SeatedKneeExtensionsState.EXTENDING,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              
              if (!stateData.calibrated[leg] && 
                  metrics.kneeAngle > CALIBRATION_ANGLE_THRESHOLD &&
                  stateData.consecutiveHighAngleFrames[leg] >= 1) {
                this.calibrateLeg(stateData, leg, metrics.kneeAngle);
              }
              
              if (AUTO_DETECT_LEGS && stateData.stableFlexedFrames[leg] >= STABLE_FRAMES_FOR_LEG_SWITCH) {
                const otherLeg = leg === 'right' ? 'left' : 'right';
                if (stateData.legScores[otherLeg] > stateData.legScores[leg] + 5) {
                  stateData.activeLeg = otherLeg;
                  return false; 
                }
              }
              
              return !stateData.inInternalCooldown[leg] && 
                     metrics.isExtending && 
                     metrics.kneeAngle > (stateData.calibrated[leg] ? 
                       stateData.calibrationData[leg].minAngle : MIN_EXTENSION_ANGLE) && 
                     stateData.framesSinceLastExtended[leg] > MIN_FRAMES_IN_POSITION &&
                     metrics.positionValid;
            },
            action: (fsm, metrics) => {
              const leg = fsm.stateData.activeLeg;
              fsm.stateData.lastFlexedFrame[leg] = fsm.stateData.frameCounter;
              fsm.stateData.stableFlexedFrames[leg] = 0;
            }
          }
        ],
        
        [SeatedKneeExtensionsState.EXTENDING]: [
          {
            targetState: SeatedKneeExtensionsState.EXTENDED,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              const targetAngle = stateData.calibrated[leg] ? 
                stateData.calibrationData[leg].targetAngle : TARGET_EXTENSION_ANGLE;
                
              return metrics.kneeAngle > targetAngle && 
                     stateData.framesSinceLastFlexed[leg] > MIN_FRAMES_IN_POSITION &&
                     metrics.positionValid;
            },
            action: (fsm, metrics) => {
              const leg = fsm.stateData.activeLeg;
              fsm.stateData.lastExtendedFrame[leg] = fsm.stateData.frameCounter;
              
              fsm.stateData.maxExtensionAngle[leg] = Math.max(
                fsm.stateData.maxExtensionAngle[leg] || 0,
                metrics.kneeAngle
              );
                
              if (fsm.stateData.calibrated[leg] && 
                  fsm.stateData.maxExtensionAngle[leg] > CALIBRATION_ANGLE_THRESHOLD) {
                fsm.stateData.calibrationData[leg].targetAngle = 
                  fsm.stateData.maxExtensionAngle[leg] * 0.90;
              }
              
              fsm.stateData.inInternalCooldown[leg] = true;
              fsm.stateData.cooldownFramesRemaining[leg] = INTERNAL_COOLDOWN_FRAMES;
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              return Math.round(
                (metrics.extensionScore * 0.5) + 
                (stateData.hipStabilityScore[leg] * 0.3) + 
                (stateData.controlScore[leg] * 0.2)
              );
            }
          },
          {
            targetState: SeatedKneeExtensionsState.FLEXED,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              const minAngle = stateData.calibrated[leg] ? 
                stateData.calibrationData[leg].minAngle : MIN_EXTENSION_ANGLE;
                
              return metrics.isFlexing && metrics.kneeAngle < minAngle;
            }
          }
        ],
        
        [SeatedKneeExtensionsState.EXTENDED]: [
          {
            targetState: SeatedKneeExtensionsState.FLEXING,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              return metrics.isFlexing && 
                     metrics.kneeAngle < stateData.maxExtensionAngle[leg] - ANGLE_CHANGE_THRESHOLD &&
                     metrics.positionValid;
            },
            action: (fsm, metrics) => {
              const leg = fsm.stateData.activeLeg;
              fsm.stateData.stableExtendedFrames[leg] = 0;
            }
          },
          {
            targetState: SeatedKneeExtensionsState.FLEXED,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              const minAngle = stateData.calibrated[leg] ? 
                stateData.calibrationData[leg].minAngle : MIN_EXTENSION_ANGLE;
                
              return metrics.kneeAngle < minAngle;
            }
          }
        ],
        
        [SeatedKneeExtensionsState.FLEXING]: [
          {
            targetState: SeatedKneeExtensionsState.FLEXED,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              const minAngle = stateData.calibrated[leg] ? 
                stateData.calibrationData[leg].minAngle : MIN_EXTENSION_ANGLE;
                
              return metrics.kneeAngle < minAngle;
            },
            action: (fsm, metrics) => {
              const leg = fsm.stateData.activeLeg;
              fsm.stateData.lastFlexedFrame[leg] = fsm.stateData.frameCounter;
              
              fsm.stateData.inInternalCooldown[leg] = false;
              fsm.stateData.lastValidMetrics[leg] = null;
              fsm.stateData.lastAngles[leg] = [];
            }
          },
          {
            targetState: SeatedKneeExtensionsState.EXTENDING,
            condition: (metrics, stateData) => {
              const leg = stateData.activeLeg;
              return !stateData.inInternalCooldown[leg] && 
                     metrics.isExtending && 
                     metrics.kneeAngle > (stateData.calibrated[leg] ? 
                       stateData.calibrationData[leg].minAngle : MIN_EXTENSION_ANGLE) &&
                     metrics.positionValid;
            }
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }

  calibrateLeg(stateData, leg, kneeAngle) {
    stateData.calibrationData[leg].restingAngle = Math.max(90 - 15, kneeAngle - 50); 
    stateData.calibrationData[leg].targetAngle = kneeAngle * 0.90; 
    stateData.calibrationData[leg].minAngle = stateData.calibrationData[leg].restingAngle + 10; 
    stateData.calibrated[leg] = true;
  }
  
  setActiveLeg(leg) {
    if (this.stateData.activeLeg !== leg) {
      this.stateData.activeLeg = leg;
      
      if (this.currentState !== SeatedKneeExtensionsState.FLEXED) {
        this.transitionTo(SeatedKneeExtensionsState.FLEXED);
      }
    }
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    this.stateData.frameCounter++;
    
    if (!worldLandmarks) {
      this.stateData.consecutiveEmptyFrames.right++;
      this.stateData.consecutiveEmptyFrames.left++;
      
      if (this.stateData.lastValidMetrics[this.stateData.activeLeg]) {
        const metrics = {...this.stateData.lastValidMetrics[this.stateData.activeLeg]};
        metrics.isExtending = false;
        metrics.isFlexing = false;
        metrics.validLandmarks = false;
        return metrics;
      }
      
      return {
        kneeAngle: 0,
        isExtending: false,
        isFlexing: false,
        extensionScore: 0,
        positionValid: true,
        activeLeg: this.stateData.activeLeg,
        validLandmarks: false
      };
    }
    
    this.stateData.consecutiveEmptyFrames.right = 0;
    this.stateData.consecutiveEmptyFrames.left = 0;
    
    const leftHip = worldLandmarks[23];
    const rightHip = worldLandmarks[24];
    const leftKnee = worldLandmarks[25];
    const rightKnee = worldLandmarks[26];
    const leftAnkle = worldLandmarks[27];
    const rightAnkle = worldLandmarks[28];
    
    let leftShoulder = worldLandmarks[11];
    let rightShoulder = worldLandmarks[12];
    
    let positionValid = true;
    if (leftShoulder && rightShoulder && leftHip && rightHip) {
      const shoulderHipVerticalRatio = Math.abs((leftShoulder.y + rightShoulder.y)/2 - (leftHip.y + rightHip.y)/2);
      const shoulderHipHorizontalDist = Math.abs((leftShoulder.x + rightShoulder.x)/2 - (leftHip.x + rightHip.x)/2);
      
      const positionRatio = shoulderHipVerticalRatio / (shoulderHipHorizontalDist + 0.001);
      
      positionValid = positionRatio > SEATED_POSTURE_THRESHOLD;
    }
    
    const legMetrics = {};
    
    if (rightHip && rightKnee && rightAnkle) {
      legMetrics.right = this.processLegMetrics('right', rightHip, rightKnee, rightAnkle, positionValid);
    }
    
    if (leftHip && leftKnee && leftAnkle) {
      legMetrics.left = this.processLegMetrics('left', leftHip, leftKnee, leftAnkle, positionValid);
    }
    
    for (const leg of ['right', 'left']) {
      if (this.stateData.inInternalCooldown[leg]) {
        this.stateData.cooldownFramesRemaining[leg]--;
        if (this.stateData.cooldownFramesRemaining[leg] <= 0) {
          this.stateData.inInternalCooldown[leg] = false;
        }
      }
      
      this.stateData.framesSinceLastFlexed[leg]++;
      this.stateData.framesSinceLastExtended[leg]++;
    }
    
    if (AUTO_DETECT_LEGS && legMetrics.right && legMetrics.left) {
      const rightChange = Math.abs(legMetrics.right.kneeAngle - this.stateData.previousKneeAngles.right);
      const leftChange = Math.abs(legMetrics.left.kneeAngle - this.stateData.previousKneeAngles.left);
      
      this.stateData.legScores.right = this.stateData.legScores.right * 0.9 + rightChange;
      this.stateData.legScores.left = this.stateData.legScores.left * 0.9 + leftChange;
      
      this.stateData.previousKneeAngles.right = legMetrics.right.kneeAngle;
      this.stateData.previousKneeAngles.left = legMetrics.left.kneeAngle;
    }
    
    const activeLeg = this.stateData.activeLeg;
    if (legMetrics[activeLeg]) {
      return legMetrics[activeLeg];
    } else {
      if (this.stateData.lastValidMetrics[activeLeg]) {
        const metrics = {...this.stateData.lastValidMetrics[activeLeg]};
        metrics.isExtending = false;
        metrics.isFlexing = false;
        metrics.validLandmarks = false;
        return metrics;
      }
      
      return {
        kneeAngle: 0,
        isExtending: false,
        isFlexing: false,
        extensionScore: 0,
        positionValid: true,
        activeLeg: activeLeg,
        validLandmarks: false
      };
    }
  }
  
  processLegMetrics(leg, hip, knee, ankle, positionValid) {
    const rawKneeAngle = this.calculateJointAngle(hip, knee, ankle);
    
    const kneeAngle = this.applyKalmanFilter(this.stateData.kalmanFilters[leg], rawKneeAngle);
    
    if (!this.stateData.lastAngles[leg]) this.stateData.lastAngles[leg] = [];
    if (this.stateData.lastAngles[leg].length > 5) this.stateData.lastAngles[leg].shift();
    this.stateData.lastAngles[leg].push(kneeAngle);
    
    let isExtending = false;
    let isFlexing = false;
    
    if (this.stateData.lastAngles[leg].length >= 2) {
      const lastAngle = this.stateData.lastAngles[leg][this.stateData.lastAngles[leg].length - 1];
      const prevAngle = this.stateData.lastAngles[leg][this.stateData.lastAngles[leg].length - 2];
      
      const angleDiff = lastAngle - prevAngle;
      
      isExtending = angleDiff > MOVEMENT_THRESHOLD;
      isFlexing = angleDiff < -MOVEMENT_THRESHOLD;
      
      if (lastAngle > CALIBRATION_ANGLE_THRESHOLD) {
        this.stateData.consecutiveHighAngleFrames[leg]++;
      } else {
        this.stateData.consecutiveHighAngleFrames[leg] = 0;
      }
    }
    
    let controlScore = 100;
    if (this.stateData.lastAngles[leg].length > 2) {
      const angleChanges = [];
      for (let i = 1; i < this.stateData.lastAngles[leg].length; i++) {
        angleChanges.push(this.stateData.lastAngles[leg][i] - this.stateData.lastAngles[leg][i-1]);
      }
      
      const mean = angleChanges.reduce((a, b) => a + b, 0) / angleChanges.length;
      const variance = angleChanges.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b, 0) / angleChanges.length;
      controlScore = Math.max(0, 100 - (variance * 2));
    }
    
    const targetAngleForScore = this.stateData.calibrated[leg] ? 
      this.stateData.calibrationData[leg].targetAngle : TARGET_EXTENSION_ANGLE;
    const extensionScore = Math.min(100, (kneeAngle / targetAngleForScore) * 100);
    
    let hipStabilityScore = 100;
    if (this.stateData.previousHipPosition[leg]) {
      const hipMovement = this.calculateDistance3D(this.stateData.previousHipPosition[leg], hip);
      hipStabilityScore = Math.max(0, 100 - (hipMovement / JOINT_STABILITY_THRESHOLD) * 100);
    }
    
    this.stateData.previousHipPosition[leg] = { ...hip };
    
    if (this.currentState === SeatedKneeExtensionsState.FLEXED && leg === this.stateData.activeLeg) {
      this.stateData.stableFlexedFrames[leg]++;
    } else if (leg === this.stateData.activeLeg) {
      this.stateData.stableFlexedFrames[leg] = 0;
    }
    
    if (this.currentState === SeatedKneeExtensionsState.EXTENDED && leg === this.stateData.activeLeg) {
      this.stateData.stableExtendedFrames[leg]++;
    } else if (leg === this.stateData.activeLeg) {
      this.stateData.stableExtendedFrames[leg] = 0;
    }
    
    this.stateData.hipStabilityScore[leg] = hipStabilityScore;
    this.stateData.controlScore[leg] = controlScore;
    this.stateData.extensionScore[leg] = extensionScore;
    
    const metrics = {
      kneeAngle,
      rawKneeAngle,
      isExtending,
      isFlexing,
      extensionScore,
      hipStabilityScore,
      controlScore,
      activeLeg: leg,
      positionValid,
      validLandmarks: true
    };
    
    this.stateData.lastValidMetrics[leg] = metrics;
    
    return metrics;
  }
  
  updateFormScores(metrics, transitioned) {
    if (!metrics || !metrics.validLandmarks) return;
    
    const leg = metrics.activeLeg;
    
    const extensionWeight = 0.5;
    const stabilityWeight = 0.3;
    const controlWeight = 0.2;
    
    const overallScore = Math.round(
      (metrics.extensionScore * extensionWeight) +
      (this.stateData.hipStabilityScore[leg] * stabilityWeight) +
      (this.stateData.controlScore[leg] * controlWeight)
    );
    
    const alpha = 0.3; 
    this.formScores.overall = Math.round(
      (1 - alpha) * this.formScores.overall + alpha * overallScore
    );
    
    this.formScores.rangeOfMotion = metrics.extensionScore;
    this.formScores.balance = this.stateData.hipStabilityScore[leg];
    this.formScores.control = this.stateData.controlScore[leg];
  }
  
  calculateDistance3D(point1, point2) {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    const dz = point2.z - point1.z;
    return Math.sqrt(dx*dx + dy*dy + dz*dz);
  }
  
  calculateJointAngle(a, b, c) {
    const vectorBA = {
      x: a.x - b.x,
      y: a.y - b.y,
      z: a.z - b.z
    };
    
    const vectorBC = {
      x: c.x - b.x,
      y: c.y - b.y,
      z: c.z - b.z
    };
    
    const dotProduct = vectorBA.x * vectorBC.x + vectorBA.y * vectorBC.y + vectorBA.z * vectorBC.z;
    
    const magnitudeBA = Math.sqrt(vectorBA.x * vectorBA.x + vectorBA.y * vectorBA.y + vectorBA.z * vectorBA.z);
    const magnitudeBC = Math.sqrt(vectorBC.x * vectorBC.x + vectorBC.y * vectorBC.y + vectorBC.z * vectorBC.z);
    
    const cosAngle = Math.max(-1, Math.min(1, dotProduct / (magnitudeBA * magnitudeBC)));
    const angle = Math.acos(cosAngle) * (180 / Math.PI);
    
    return angle;
  }
  
  applyKalmanFilter(filter, measurement) {
    filter.P = filter.P + filter.Q;
    
    const K = filter.P / (filter.P + filter.R);
    filter.x = filter.x + K * (measurement - filter.x);
    filter.P = (1 - K) * filter.P;
    
    return filter.x;
  }
} 